import React, { useEffect } from 'react';
import styles from './CampoSeletor.module.css';
import Icon from '@/components/Icon'
import {
  mdiChevronDown,
  mdiClose,
  mdiInformationSlabCircleOutline,
  mdiLoading,
  mdiMagnify,
} from '@mdi/js';
import CampoTexto from './CampoTexto';
import ReactDOMServer from 'react-dom/server';
import HBox from '../layout/HBox';
import { Tooltip } from '../layout/Tooltip';
import Botao from '../Botao';
import { useDebounce } from '@/hooks/useDebounce';
import { Combobox } from '@base-ui/react/combobox';
import { InsideModalContext, getInsideModalZIndex } from '../layout/Modal';

export type CampoSeletorOption = {
  id: string;
  label: React.ReactNode;
  group?: string;
  groupId?: string;
  error?: boolean;
};

type CampoSeletorOptionsProps = {
  selectedId: string | null;
  selectedGroupId?: string | null;
  onChangeSelectedId?: (index: string | null) => void;
  value: CampoSeletorOption[];
  onSelect: (value: string, groupId?: string) => void;
  onGroupSelect?: (group: string) => void;
  noSearch?: boolean;
  search: string;
  onChangeSearch: (value: string) => void;
  hasRemoteSearch?: boolean;
  loadingState?: 'idle' | 'loading' | 'error' | 'success';
};

export const CampoSeletorOptions = ({
  selectedId,
  selectedGroupId,
  onChangeSelectedId,
  value,
  onSelect,
  onGroupSelect,
  noSearch,
  search,
  onChangeSearch,
  hasRemoteSearch = false,
  loadingState = 'idle',
}: CampoSeletorOptionsProps) => {
  const filteredOptions =
    !hasRemoteSearch && search.length > 0
      ? value?.filter(
        it =>
          !it.label ||
          ReactDOMServer.renderToString(it.label)
            .toLowerCase()
            .includes(search.toLowerCase()) ||
          (it.group && it.group.toLowerCase().includes(search.toLowerCase()))
      )
      : value;

  const groups = filteredOptions?.reduce<{
    [key: string]: { gid?: string, label?: string, options: CampoSeletorOption[] };
  }>((acc, it) => {
    const key: string = it.groupId ?? it.group ?? '';
    if (!acc[key])
      acc[key] = { gid: it.groupId, label: it.group, options: [] };

    acc[key].options.push(it);
    return acc;
  }, {});

  return (
    <div className={styles.dropdownContainer} role="dropdown">
      {!noSearch && (
        <div style={{ position: 'sticky', top: 0, zIndex: 1, marginBottom: '4px' }}>
          <CampoTexto
            prepend={
              loadingState === 'loading'
                ? <Icon path={mdiLoading} spin size={1} color="var(--tc-color-gray-400)" />
                : loadingState === 'error'
                  ? <Icon path={mdiClose} size={1} color="var(--tc-color-danger)" />
                  : <Icon path={mdiMagnify} size={1} color="var(--tc-color-gray-400)" />
            }
            placeholder="Pesquisar"
            onClick={(e: any) => {
              console.log('input click');
            }}
            value={search}
            onChange={onChangeSearch}
          />
        </div>
      )}
      {search.length && !filteredOptions?.length ? (
        <div className={styles.noResults}>(Nenhum resultado encontrado)</div>
      ) : null}
      {groups
        ? Object.entries(groups).map(([key, gOptions], gidx) => {
          return (
            <div
              key={gidx}
            >
              {key && (
                <div
                  tabIndex={onGroupSelect ? 0 : undefined}
                  className={[
                    styles.option,
                    styles.group,
                    selectedGroupId == gOptions.gid ? styles.selected : null,
                  ].join(' ')}
                  onClick={(e: any) => {
                    e.stopPropagation();
                    if (onGroupSelect && key.length > 0) {
                      e.preventDefault();
                    }
                    if (onGroupSelect) {
                      onGroupSelect(gOptions.gid ?? gOptions.label ?? key);
                    }
                  }}
                  onKeyDown={(e) => {
                    e.stopPropagation();
                    if (onGroupSelect && key.length > 0) {
                      e.preventDefault();
                    }

                    if (e.code.toLowerCase() === 'enter' && onGroupSelect) {
                      onGroupSelect(gOptions.gid ?? gOptions.label ?? key);
                    }
                  }}>{gOptions.label}</div>
              )}
              {gOptions.options.map((option, index) => (
                <div
                  tabIndex={0}
                  key={option.id}
                  className={[
                    styles.option,
                    selectedId == option.id ? styles.selected : null,
                    key.length > 0 ? styles.grouped : null,
                    option.error ? styles.error : null,
                  ].join(' ')}
                  onClick={() => {
                    onSelect?.(option.id, option.groupId);
                  }}
                >
                  {option.label}
                </div>
              ))}
            </div>
          );
        })
        : null}
    </div>
  );
};

type CampoSeletorProps = {
  value?: string | null;
  onChange?: (value: string | null) => void;
  label?: string;
  placeholder?: string;
  type?: string;
  required?: boolean;
  options: CampoSeletorOption[];
  disabled?: boolean;
  error?: string;
  noSearch?: boolean;
  noBorder?: boolean;
  tooltipText?: React.ReactNode;
  tooltipPosition?: 'top' | 'bottom' | 'left' | 'right';
  instanceId?: string;
  nullable?: boolean;
  initialSearch?: string;
  onChangeSearch?: (value: string) => void;
  hasRemoteSearch?: boolean;
  loadingState?: 'idle' | 'loading' | 'error' | 'success';
  [key: string]: any;
};

// Mapeia id → label texto (para filtro interno do Combobox)
function buildLabelMap(options: CampoSeletorOption[]): Record<string, string> {
  const map: Record<string, string> = {};
  for (const opt of options) {
    if (!opt.label) {
      map[opt.id] = '';
    } else if (typeof opt.label === 'string') {
      map[opt.id] = opt.label;
    } else {
      try {
        map[opt.id] = ReactDOMServer.renderToString(opt.label as React.ReactElement);
      } catch {
        map[opt.id] = '';
      }
    }
  }
  return map;
}

// Agrupa as opções por group/groupId
function buildGroups(options: CampoSeletorOption[]) {
  return options.reduce<{
    [key: string]: { gid?: string; label?: string; options: CampoSeletorOption[] };
  }>((acc, it) => {
    const key = it.groupId ?? it.group ?? '';
    if (!acc[key]) acc[key] = { gid: it.groupId, label: it.group, options: [] };
    acc[key].options.push(it);
    return acc;
  }, {});
}

export default function CampoSeletor({
  value,
  onChange,
  label,
  placeholder,
  type = 'text',
  required,
  options = [],
  disabled,
  error,
  noSearch,
  tooltipText,
  tooltipPosition,
  noBorder,
  instanceId,
  nullable,
  initialSearch,
  onChangeSearch,
  hasRemoteSearch,
  loadingState,
  ...props
}: Readonly<CampoSeletorProps>) {
  const insideModal = React.useContext(InsideModalContext);
  const [inputValue, setInputValue] = React.useState<string>(initialSearch ?? '');

  const debouncedInput = useDebounce(inputValue, 300);
  useEffect(() => {
    onChangeSearch?.(debouncedInput);
  }, [debouncedInput]);

  const labelMap = React.useMemo(() => buildLabelMap(options), [options]);

  // Quando hasRemoteSearch=true o filtro é feito externamente; passamos as options
  // sem filtrar. Quando false, o Combobox filtra internamente via filter prop.
  const filter = hasRemoteSearch
    ? null
    : (itemId: string, query: string) => {
      const text = labelMap[String(itemId)] ?? '';
      // Busca também no nome do grupo
      const opt = options.find(o => String(o.id) === String(itemId));
      const groupText = opt?.group ?? '';
      return (
        text.toLowerCase().includes(query.toLowerCase()) ||
        groupText.toLowerCase().includes(query.toLowerCase())
      );
    };

  const groups = React.useMemo(() => buildGroups(options), [options]);
  const hasGroups = Object.keys(groups).some(k => k !== '');

  const rootItems = React.useMemo(() => {
    if (hasGroups) {
      return Object.entries(groups).map(([key, g]) => ({
        gid: key,
        label: g.label,
        items: g.options.map(o => o.id),
      }));
    }
    return options.map(o => o.id);
  }, [groups, hasGroups, options]);

  return (
    <div style={{ width: '100%', position: 'relative', overflow: 'visible' }} id={instanceId}>
      <Combobox.Root
        value={value ?? null}
        onValueChange={(v) => onChange?.(v as string | null)}
        inputValue={inputValue}
        onInputValueChange={setInputValue}
        disabled={disabled}
        required={required}
        items={rootItems}
        filter={filter as any}
        itemToStringLabel={(id) => {
          const raw = labelMap[String(id)] ?? '';
          // Remover tags HTML do renderToString para exibir texto puro no input
          return raw.replace(/<[^>]*>/g, '');
        }}
        modal={false}
      >
        {(label || tooltipText) && (
          <Combobox.Label className={styles.fieldLabel}>
            <HBox stretch>
              <span>{label}</span>
              {tooltipText && (
                <Tooltip text={tooltipText} position={tooltipPosition}>
                  <Icon
                    path={mdiInformationSlabCircleOutline}
                    size={1}
                    color="var(--tc-color-info)"
                  />
                </Tooltip>
              )}
            </HBox>
          </Combobox.Label>
        )}

        <Combobox.InputGroup
          className={[
            styles.container,
            error ? styles.containerError : null,
            disabled ? styles.disabled : null,
          ].filter(Boolean).join(' ')}
          style={{ border: noBorder ? 'none' : undefined }}
        >
          <div className={styles.selectedContainer}>
            <Combobox.Value>
              {(selectedValue) => {
                if (selectedValue == null || selectedValue === '') {
                  return placeholder ? <span className={styles.placeholderText}>{placeholder}</span> : null;
                }
                const opt = options.find(o => String(o.id) === String(selectedValue));
                if (!opt) return placeholder ? <span className={styles.placeholderText}>{placeholder}</span> : null;
                return <span>{opt.label}</span>;
              }}
            </Combobox.Value>
          </div>

          <Combobox.Trigger className={styles.triggerOverlay} />

          {nullable && value != null && (
            <Botao
              variant="none-primary"
              icon={mdiClose}
              style={{ position: 'relative', zIndex: 1 }}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                onChange?.(null);
                setInputValue('');
              }}
            />
          )}

          <div className={styles.arrow}>
            {loadingState === 'loading' ? (
              <Icon path={mdiLoading} spin size={1} />
            ) : loadingState === 'error' ? (
              <Icon path={mdiClose} size={1} color="var(--tc-color-danger)" />
            ) : (
              <Icon path={mdiChevronDown} size={1} className={styles.arrowIcon} />
            )}
          </div>
        </Combobox.InputGroup>

        <Combobox.Portal>
          <Combobox.Positioner
            className={styles.positioner}
            style={
              insideModal
                ? { zIndex: getInsideModalZIndex(insideModal) }
                : undefined
            }
            side="bottom"
            align="start"
            sideOffset={4}
          >
            <Combobox.Popup className={styles.popup}>
              {!noSearch && (
                <div className={styles.searchWrapper}>
                  <div className={styles.searchInputWrapper}>
                    {loadingState === 'loading' ? (
                      <Icon path={mdiLoading} spin size={1} color="var(--tc-color-gray-400)" />
                    ) : loadingState === 'error' ? (
                      <Icon path={mdiClose} size={1} color="var(--tc-color-danger)" />
                    ) : (
                      <Icon path={mdiMagnify} size={1} color="var(--tc-color-gray-400)" />
                    )}
                    <Combobox.Input className={styles.searchInput} placeholder="Pesquisar" />
                  </div>
                </div>
              )}

              <Combobox.Empty className={styles.noResults}>
                (Nenhum resultado encontrado)
              </Combobox.Empty>

              <Combobox.List className={styles.dropdownContainer}>
                {hasGroups
                  ? (group: { gid: string; label?: string; items: string[] }) => (
                    <Combobox.Group key={group.gid} items={group.items}>
                      {group.gid && (
                        <Combobox.GroupLabel className={`${styles.option} ${styles.group}`}>
                          {group.label}
                        </Combobox.GroupLabel>
                      )}
                      <Combobox.Collection>
                        {(id: string) => {
                          const opt = options.find(o => String(o.id) === String(id));
                          if (!opt) return null;
                          return (
                            <Combobox.Item
                              key={id}
                              value={id}
                              className={({ highlighted, selected }) =>
                                [
                                  styles.option,
                                  styles.grouped,
                                  opt.error ? styles.optionError : null,
                                  highlighted ? styles.highlighted : null,
                                  selected ? styles.selected : null,
                                ].filter(Boolean).join(' ')
                              }
                            >
                              {opt.label}
                            </Combobox.Item>
                          );
                        }}
                      </Combobox.Collection>
                    </Combobox.Group>
                  )
                  : (id: string) => {
                    const opt = options.find(o => String(o.id) === String(id));
                    if (!opt) return null;
                    return (
                      <Combobox.Item
                        key={opt.id}
                        value={opt.id}
                        className={({ highlighted, selected }) =>
                          [
                            styles.option,
                            opt.error ? styles.optionError : null,
                            highlighted ? styles.highlighted : null,
                            selected ? styles.selected : null,
                          ].filter(Boolean).join(' ')
                        }
                      >
                        {opt.label}
                      </Combobox.Item>
                    );
                  }}
              </Combobox.List>
            </Combobox.Popup>
          </Combobox.Positioner>
        </Combobox.Portal>
      </Combobox.Root>
      {error && <div className={styles.errorMessage}>{error}</div>}
    </div>
  );
}
